import { createSlice, PayloadAction, ThunkAction } from '@reduxjs/toolkit';
import { persistStore } from 'redux-persist';
import { RootState, store } from './store';

interface EligibilityState {
  FTSApplicantIsEligible: boolean | null;
  RemainingTranslations: number | null;
  ftsApplicantId: string | null;
  IsReturningApplicant: boolean | null;
  FTSApplicantEligibilityResponseDescription: string | null; // Use this field for error messages
}

const getPersistedEligibilityState = (): EligibilityState => {
  try {
    const persistedState = localStorage.getItem('persist:root');
    if (!persistedState)
      return {
        FTSApplicantIsEligible: null,
        RemainingTranslations: null,
        ftsApplicantId: null,
        IsReturningApplicant: null,
        FTSApplicantEligibilityResponseDescription: null,
      };
    const parsedState = JSON.parse(persistedState);
    return parsedState?.eligibility
      ? JSON.parse(parsedState.eligibility)
      : {
          FTSApplicantIsEligible: null,
          RemainingTranslations: null,
          ftsApplicantId: null,
          IsReturningApplicant: null,
          FTSApplicantEligibilityResponseDescription: null,
        };
  } catch (error) {
    console.error('Error parsing persisted eligibility state:', error);
    return {
      FTSApplicantIsEligible: null,
      RemainingTranslations: null,
      ftsApplicantId: null,
      IsReturningApplicant: null,
      FTSApplicantEligibilityResponseDescription: null,
    };
  }
};

const initialState: EligibilityState = getPersistedEligibilityState();

const eligibilitySlice = createSlice({
  name: 'eligibility',
  initialState,
  reducers: {
    setEligibility(state, action: PayloadAction<boolean>) {
      state.FTSApplicantIsEligible = action.payload;
    },
    setRemainingTranslations(state, action: PayloadAction<number>) {
      state.RemainingTranslations = action.payload;
    },
    setFtsApplicantId(state, action: PayloadAction<string | null>) {
      state.ftsApplicantId = action.payload;
    },
    setIsReturningApplicant(state, action: PayloadAction<boolean | null>) {
      state.IsReturningApplicant = action.payload;
    },
    setFTSApplicantEligibilityResponseDescription(
      state,
      action: PayloadAction<string | null>
    ) {
      state.FTSApplicantEligibilityResponseDescription = action.payload;
    },
    resetEligibility(state) {
      state.FTSApplicantIsEligible = null;
      state.RemainingTranslations = null;
      state.ftsApplicantId = null;
      state.IsReturningApplicant = null;
      state.FTSApplicantEligibilityResponseDescription = null;
    },
  },
});

export const resetEligibilityAndPersist =
  (): ThunkAction<void, RootState, unknown, any> => async (dispatch) => {
    dispatch(resetEligibility());
    persistStore(store).purge();
  };

export const {
  setEligibility,
  setRemainingTranslations,
  setFtsApplicantId,
  setIsReturningApplicant,
  setFTSApplicantEligibilityResponseDescription,
  resetEligibility,
} = eligibilitySlice.actions;

export default eligibilitySlice.reducer;
